import { useMutation } from '@apollo/client';
import { useNavigate } from '@tanstack/react-router';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { appLinks } from 'src/routing/appLinks';
import type {
  createBuySellOrder as CreateBuySellOrderData,
  createBuySellOrderFromTemplate as CreateBuySellOrderFromTemplateData,
  createBuySellOrderFromTemplateVariables as CreateBuySellOrderFromTemplateVariables,
  createBuySellOrderTemplate as CreateBuySellOrderTemplateData,
  createBuySellOrderTemplateVariables as CreateBuySellOrderTemplateVariables,
  createBuySellOrderVariables as CreateBuySellOrderVariables,
  createSalesOrder as CreateSalesOrderData,
  createSalesOrderFromTemplate as CreateSalesOrderFromTemplateData,
  createSalesOrderFromTemplateVariables as CreateSalesOrderFromTemplateVariables,
  createSalesOrderTemplate as CreateSalesOrderTemplateData,
  createSalesOrderTemplateVariables as CreateSalesOrderTemplateVariables,
  createSalesOrderVariables as CreateSalesOrderVariables,
} from 'types/graphql';

export const CREATE_SALES_ORDER_MUTATION = gql`
  mutation createSalesOrder($input: CreateSalesOrderInput!) {
    createSalesOrder(input: $input) {
      id
      slug
    }
  }
`;

export const CREATE_BUY_SELL_ORDER_MUTATION = gql`
  mutation createBuySellOrder($input: CreateBuySellOrderInput!) {
    createBuySellOrder(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_SALES_ORDER_TEMPLATE_MUTATION = gql`
  mutation createSalesOrderTemplate($input: CreateBlankSalesOrderTemplateInput!) {
    createBlankSalesOrderTemplate(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_SALES_ORDER_FROM_TEMPLATE_MUTATION = gql`
  mutation createSalesOrderFromTemplate($orderTemplateId: Int!) {
    createSalesOrderFromTemplate(orderTemplateId: $orderTemplateId) {
      id
      slug
    }
  }
`;

export const CREATE_BUY_SELL_ORDER_TEMPLATE_MUTATION = gql`
  mutation createBuySellOrderTemplate($input: CreateBlankBuySellOrderTemplateInput!) {
    createBlankBuySellOrderTemplate(input: $input) {
      id
      slug
    }
  }
`;

export const CREATE_BUY_SELL_ORDER_FROM_TEMPLATE_MUTATION = gql`
  mutation createBuySellOrderFromTemplate($orderTemplateId: Int!) {
    createBuySellOrderFromTemplate(orderTemplateId: $orderTemplateId) {
      id
      slug
    }
  }
`;

export function useCreateSalesOrder() {
  const [createSalesOrder] = useMutation<CreateSalesOrderData, CreateSalesOrderVariables>(CREATE_SALES_ORDER_MUTATION);
  const navigate = useNavigate();

  return () => {
    createSalesOrder({
      variables: { input: { status: 'DRAFT' } },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the sales order.'),
      onCompleted: ({ createSalesOrder }) => {
        void navigate(appLinks.salesOrder({ slug: createSalesOrder.slug }));
      },
    });
  };
}

export function useCreateBuySellOrder() {
  const [createBuySellOrder] = useMutation<CreateBuySellOrderData, CreateBuySellOrderVariables>(
    CREATE_BUY_SELL_ORDER_MUTATION
  );
  const navigate = useNavigate();

  return () => {
    createBuySellOrder({
      variables: { input: {} },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the buy-sell order.'),
      onCompleted: ({ createBuySellOrder }) => {
        void navigate(appLinks.buySellOrder({ slug: createBuySellOrder.slug }));
      },
    });
  };
}

export function useCreateSalesOrderTemplate() {
  const [createSalesOrderTemplate] = useMutation<CreateSalesOrderTemplateData, CreateSalesOrderTemplateVariables>(
    CREATE_SALES_ORDER_TEMPLATE_MUTATION
  );
  const navigate = useNavigate();

  return (name: string) => {
    createSalesOrderTemplate({
      variables: { input: { name } },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the sales order template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createBlankSalesOrderTemplate;

      void navigate(appLinks.salesOrder({ slug }));
    });
  };
}

export function useCreateBuySellOrderTemplate() {
  const [createBuySellOrderTemplate] = useMutation<CreateBuySellOrderTemplateData, CreateBuySellOrderTemplateVariables>(
    CREATE_BUY_SELL_ORDER_TEMPLATE_MUTATION
  );
  const navigate = useNavigate();

  return (name: string) => {
    createBuySellOrderTemplate({
      variables: { input: { name } },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the buy-sell order template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createBlankBuySellOrderTemplate;
      void navigate(appLinks.buySellOrder({ slug }));
    });
  };
}

export function useCreateSalesOrderFromTemplate() {
  const [createSalesOrderFromTemplate] = useMutation<
    CreateSalesOrderFromTemplateData,
    CreateSalesOrderFromTemplateVariables
  >(CREATE_SALES_ORDER_FROM_TEMPLATE_MUTATION);
  const navigate = useNavigate();

  return (orderTemplateId: number) => {
    createSalesOrderFromTemplate({
      variables: { orderTemplateId },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the sales order from the template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createSalesOrderFromTemplate;
      void navigate(appLinks.salesOrder({ slug }));
    });
  };
}

export function useCreateBuySellOrderFromTemplate() {
  const [createBuySellOrderFromTemplate] = useMutation<
    CreateBuySellOrderFromTemplateData,
    CreateBuySellOrderFromTemplateVariables
  >(CREATE_BUY_SELL_ORDER_FROM_TEMPLATE_MUTATION);
  const navigate = useNavigate();

  return (orderTemplateId: number) => {
    createBuySellOrderFromTemplate({
      variables: { orderTemplateId },
      onError: (error) => notifyUnlessSurfaced(error, 'Could not create the buy-sell order from the template.'),
    }).then(({ data }) => {
      if (!data) return;
      const { slug } = data.createBuySellOrderFromTemplate;
      void navigate(appLinks.buySellOrder({ slug }));
    });
  };
}
